import { Bar, Column, Pie } from '@ant-design/charts'
import { Spin } from 'antd'
import React from 'react'
import * as fcIcon from 'react-icons/all'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { NumberFormat } from '../../../../components'
import { translate } from '../../../../languages'
import { RootState } from '../../../../stores'
import {
  loadFuelStatistic,
  setFuelStatisticFilter,
  setFuelStatisticLoaded,
} from '../../../../stores/actions'
import { StatisticFilterComponent } from './StatisticFilterComponent'

const StyledStatisticTitle = styled.div`
  width: 100%;
  font-size: 1.6rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
`

const StyledFuelStatistic = styled.div`
  width: 100%;
`

const StyledFuelStatisticChart = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
`

const StyledLoadingFuelStatistic = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
`

const StyledOverview = styled.div<{
  style: {
    isDesktopScreen: boolean
  }
}>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`

const StyledOverViewCard = styled.div<{
  style: {
    isDesktopScreen: boolean
  }
}>`
  width: ${(props) => (props.style.isDesktopScreen ? 250 : 150)}px;
  margin: 5px 0;
  display: flex;
  box-shadow: rgba(0, 0, 0, 0.25) 0px 14px 28px,
    rgba(0, 0, 0, 0.22) 0px 10px 10px;
  border-radius: 10px;
`

const StyledCardContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
  margin-left: 10px;
  width: 100%;
`

const StyledCardTitle = styled.div`
  width: 140px;
  font-weight: bold;
`

const StyledGroupBarContainer = styled.div<{
  style: {
    isSuperLargeScreen: boolean
  }
}>`
  width: 100%;
  max-width: ${(props) => (props.style.isSuperLargeScreen ? 1400 : 1650)}px;
  margin-top: 30px;
`

const StyledGroupBarTitle = styled.div`
  font-weight: bold;
  width: 100%;
  text-align: center;
`

const StyledPieContainer = styled.div`
  width: 375px;
  margin-top: 50px;
`

const StyledPieTitle = styled.div`
  font-weight: bold;
  width: 100%;
  text-align: center;
`

export const FuelStatisticComponent: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { isFuelsLoaded, fuels } = useSelector((state: RootState) => state.fuel)
  const { fuelStatistic, isFuelStatisticLoaded, fuelStatisticFilter } =
    useSelector((state: RootState) => state.fuelStatistic)
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })
  const isLargeScreen = useMediaQuery({ minWidth: '1000px' })
  const isSuperLargeScreen = useMediaQuery({ minWidth: '1650px' })

  const [filter, setFilter] = React.useState(fuelStatisticFilter)

  React.useEffect(() => {
    const autoLoad = setInterval(() => {
      dispatch(
        setFuelStatisticFilter({
          ...fuelStatisticFilter,
          endTime: new Date().getTime(),
        })
      )
      dispatch(loadFuelStatistic())
    }, 10000)
    return () => clearInterval(autoLoad)
  })

  React.useEffect(() => {
    setFilter(fuelStatisticFilter)
  }, [fuelStatisticFilter])

  const pieData = fuels.map((fuel) => {
    return {
      fuel: fuel.name,
      value: parseFloat(
        fuelStatistic
          .filter((item) => item.fuel.id === fuel.id)
          .reduce((arr, curr) => arr + curr.totalVolume, 0)
          .toFixed(3)
      ),
    }
  })

  const pieConfig = {
    appendPadding: 10,
    data: pieData,
    angleField: 'value',
    colorField: 'fuel',
    radius: 1,
    innerRadius: 0.64,
    label: {
      type: 'inner',
      offset: '-50%',
      content: (_ref: any) =>
        _ref.value ? parseFloat(_ref.value).toFixed(3) : _ref.value,
      style: {
        textAlign: 'center',
        fontSize: 14,
      },
    },
    color: [
      '#bd53fa',
      '#2ca02c',
      '#26e9f0',
      '#e6f703',
      '#264bee',
      '#a2c4b1',
      '#f8b22f',
      '#d62728',
      '#034d70',
      '#6b01e4',
      '#5b5c61',
      '#a7ff33',
    ],
    interactions: [{ type: 'element-selected' }, { type: 'element-active' }],
    statistic: {
      title: {
        customHtml: () => translate('fuelStatistic.total', language),
      },
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        content: fuelStatistic
          .reduce((arr, curr) => arr + curr.totalVolume, 0)
          .toFixed(3),
      },
    },
  }

  const groupBarData = React.useMemo(
    () =>
      ['revenue', 'cash', 'paid', 'debt']
        .map((field) =>
          fuels.map((fuel) => {
            const index = fuelStatistic
              .map((item) => item.fuel.id)
              .indexOf(fuel.id)
            const data = index > -1 ? fuelStatistic[index] : undefined
            return {
              fuel: fuel.name,
              type: translate(`fuelStatistic.${field}`, language),
              value: data
                ? field === 'revenue'
                  ? data.totalRevenue
                  : field === 'cash'
                  ? data.totalCash
                  : field === 'debt'
                  ? data.totalDebt
                  : data.totalRevenue - data.totalCash - data.totalDebt
                : 0,
            }
          })
        )
        .reduce((arr, curr) => [...arr, ...curr], []),
    [fuelStatistic, fuels, language]
  )

  const groupBarFormatLabel = {
    label: {
      formatter: function formatter(value: any) {
        return `${value
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${translate(
          'common.moneyType',
          language
        )})`
      },
    },
  }

  const groupBarConfig = {
    data: groupBarData,
    isGroup: true,
    xField: isLargeScreen ? 'fuel' : 'value',
    yField: isLargeScreen ? 'value' : 'fuel',
    yAxis: isLargeScreen ? groupBarFormatLabel : {},
    xAxis: isLargeScreen ? {} : groupBarFormatLabel,
    seriesField: 'type',
    labels: {
      style: {
        fill: 'red',
        opacity: 0.6,
        fontSize: 24,
      },
      rotate: true,
    },
    legend: {
      position: 'top-left',
    } as any,
    tooltip: {
      formatter: function formatter(datum: any) {
        return {
          name: datum.type,
          value: `${datum.value
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${translate(
            'common.moneyType',
            language
          )})`,
        }
      },
    },
  }

  return (
    <StyledFuelStatistic>
      <StyledStatisticTitle>
        {translate('fuelStatistic.title', language)}
      </StyledStatisticTitle>
      <StyledOverview style={{ isDesktopScreen }}>
        <StyledOverViewCard style={{ isDesktopScreen }}>
          <fcIcon.FcMoneyTransfer size={isDesktopScreen ? 55 : 35} />
          <StyledCardContent>
            <StyledCardTitle>
              {translate('fuelStatistic.revenue', language)}
            </StyledCardTitle>
            <NumberFormat
              suffixKey="common.moneyType"
              value={fuelStatistic.reduce(
                (acc, curr) => acc + curr.totalRevenue,
                0
              )}
            />
          </StyledCardContent>
        </StyledOverViewCard>
        <StyledOverViewCard style={{ isDesktopScreen }}>
          <fcIcon.GiMoneyStack
            size={isDesktopScreen ? 55 : 35}
            style={{ color: '#65ff3e' }}
          />
          <StyledCardContent>
            <StyledCardTitle>
              {translate('fuelStatistic.cash', language)}
            </StyledCardTitle>
            <NumberFormat
              suffixKey="common.moneyType"
              value={fuelStatistic.reduce(
                (acc, curr) => acc + curr.totalCash,
                0
              )}
            />
          </StyledCardContent>
        </StyledOverViewCard>
        <StyledOverViewCard style={{ isDesktopScreen }}>
          <fcIcon.FcSimCardChip size={isDesktopScreen ? 55 : 35} />
          <StyledCardContent>
            <StyledCardTitle>
              {translate('fuelStatistic.paid', language)}
            </StyledCardTitle>
            <NumberFormat
              suffixKey="common.moneyType"
              value={fuelStatistic.reduce(
                (acc, curr) =>
                  acc + curr.totalRevenue - curr.totalDebt - curr.totalCash,
                0
              )}
            />
          </StyledCardContent>
        </StyledOverViewCard>
        <StyledOverViewCard style={{ isDesktopScreen }}>
          <fcIcon.FcDebt size={isDesktopScreen ? 55 : 35} />
          <StyledCardContent>
            <StyledCardTitle>
              {translate('fuelStatistic.debt', language)}
            </StyledCardTitle>
            <NumberFormat
              suffixKey="common.moneyType"
              value={fuelStatistic.reduce(
                (acc, curr) => acc + curr.totalDebt,
                0
              )}
            />
          </StyledCardContent>
        </StyledOverViewCard>
      </StyledOverview>
      <StatisticFilterComponent
        onSearch={() => {
          dispatch(setFuelStatisticFilter(filter))
          dispatch(setFuelStatisticLoaded(false))
          dispatch(loadFuelStatistic())
        }}
        rangeValue={{ startTime: filter.startTime, endTime: filter.endTime }}
        selectedStationIds={filter.stationIds}
        onRangePickerChange={(e) => {
          if (e && e.length === 2) {
            setFilter({
              ...filter,
              startTime: e && e[0] ? e[0].toDate().getTime() : filter.startTime,
              endTime: e && e[1] ? e[1].toDate().getTime() : filter.endTime,
            })
          }
        }}
        onSelectStationChange={(e) => {
          setFilter({
            ...filter,
            stationIds: e,
          })
        }}
      />
      {!isFuelsLoaded || !isFuelStatisticLoaded ? (
        <StyledLoadingFuelStatistic>
          <Spin
            style={{ fontSize: 15 }}
            size="large"
            tip={translate('fuelStatistic.loading.tip', language)}
          />
        </StyledLoadingFuelStatistic>
      ) : (
        <StyledFuelStatisticChart>
          <StyledGroupBarContainer style={{ isSuperLargeScreen }}>
            <StyledGroupBarTitle>
              {`${translate(
                'fuelStatistic.groupBar.title',
                language
              )} (${translate('common.moneyType', language)})`}
            </StyledGroupBarTitle>
            {isLargeScreen ? (
              <Column {...groupBarConfig} style={{ width: '100%' }} />
            ) : (
              <Bar {...groupBarConfig} style={{ width: '100%' }} />
            )}
          </StyledGroupBarContainer>
          <StyledPieContainer>
            <StyledPieTitle>
              {`${translate('fuelStatistic.pie.title', language)} (${translate(
                'fuel.unit.litter',
                language
              )})`}
            </StyledPieTitle>
            <Pie {...pieConfig} style={{ width: '100%', marginTop: '-50px' }} />
          </StyledPieContainer>
        </StyledFuelStatisticChart>
      )}
    </StyledFuelStatistic>
  )
}
